import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  XMarkIcon,
  CubeIcon,
  TagIcon,
  ClipboardDocumentListIcon,
  TableCellsIcon,
  GlobeAltIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  BuildingStorefrontIcon,
  BellIcon,
  Cog6ToothIcon,
  EllipsisHorizontalIcon,
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  ShareIcon,
  UsersIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { buildRestaurantFrontendOrigin } from '../utils/restaurantUtils';
import { getStaffRoleFromToken } from '../utils/staffRoleUtils';
import {
  LayoutProvider,
  useLayoutSidebar,
} from '../context/LayoutContext';

interface LayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onPageChange: (page: string) => void;
  onShowSubscriptionModal?: () => void;
}

const getStoreUrl = (): string => {
  try {
    const restaurantData = localStorage.getItem('restaurantDetails');
    const details = restaurantData ? JSON.parse(restaurantData) : null;
    return buildRestaurantFrontendOrigin(details || undefined);
  } catch (e) {
    return buildRestaurantFrontendOrigin(undefined);
  }
};

const StoreUrlBlock: React.FC = () => {
  const url = React.useMemo(() => getStoreUrl(), []);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const el = document.createElement('textarea');
        el.value = url;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // ignore
    }
  };

  const handleShare = async () => {
    try {
      // Use Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: 'Visit My Store',
          text: 'Check out my online store!',
          url: url,
        });
      } else {
        // Fallback to copying to clipboard
        await handleCopy();
      }
    } catch (error) {
      // User cancelled or error occurred, fallback to copy
      if ((error as Error).name !== 'AbortError') {
        await handleCopy();
      }
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Share your store URL or visit it to preview the live site.</p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <input
          value={url}
          readOnly
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-800 bg-gray-50"
        />
        <div className="flex gap-2">
          <button
            onClick={handleCopy}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            onClick={handleShare}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            <ShareIcon className="h-4 w-4 mr-1.5" />
            Share
          </button>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 rounded-md shadow-sm"
          >
            Visit Site
            <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />
          </a>
        </div>
      </div>
    </div>
  );
};

const LayoutShell: React.FC<LayoutProps> = ({ children, currentPage, onPageChange, onShowSubscriptionModal }) => {
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isVisitModalOpen, setIsVisitModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const { desktopSidebarCollapsed, setDesktopSidebarCollapsed } = useLayoutSidebar();
  const { user, subscription, logout, hasProPlan } = useAuth();
  const {
    isSupported,
    isPermissionGranted,
    initialize
  } = useNotifications();

  const canViewStoreLiveNav = React.useMemo(() => {
    const role = getStaffRoleFromToken();
    return role === 'shopOwner' || role === 'manager';
  }, [user?.mobile]);

  // Scroll main content container to top whenever the current page changes
  React.useEffect(() => {
    try {
      const contentEl = document.querySelector('.mobile-content');
      if (contentEl instanceof HTMLElement) {
        contentEl.scrollTop = 0;
      }
      // Fallback for any window-based scrolling
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      // ignore scrolling errors
    }
  }, [currentPage]);

  // Auto-open visit modal once after signup or payment success (owner / manager only)
  React.useEffect(() => {
    if (!canViewStoreLiveNav) return;

    try {
      const pending = localStorage.getItem('visitSiteModalPending');
      if (pending === '1') {
        setIsVisitModalOpen(true);
        localStorage.removeItem('visitSiteModalPending');
      }
    } catch (_) {}

    const handleShowStoreLiveModal = () => {
      const role = getStaffRoleFromToken();
      if (role !== 'shopOwner' && role !== 'manager') return;
      setIsVisitModalOpen(true);
      localStorage.removeItem('visitSiteModalPending');
    };

    window.addEventListener('showStoreLiveModal', handleShowStoreLiveModal);

    return () => {
      window.removeEventListener('showStoreLiveModal', handleShowStoreLiveModal);
    };
  }, [canViewStoreLiveNav]);

  React.useEffect(() => {
    if (!canViewStoreLiveNav && isVisitModalOpen) {
      setIsVisitModalOpen(false);
    }
  }, [canViewStoreLiveNav, isVisitModalOpen]);

  // Handle browser back button to close visit modal
  React.useEffect(() => {
    if (!isVisitModalOpen) return;

    const handlePopState = () => {
      setIsVisitModalOpen(false);
    };

    // Push history state when modal opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isVisitModalOpen]);

  // Handle browser back button to close more menu
  React.useEffect(() => {
    if (!showMoreMenu) return;

    const handlePopState = () => {
      setShowMoreMenu(false);
    };

    // Push history state when menu opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showMoreMenu]);


  const allNavigation = [
    // { name: 'Dashboard', href: 'dashboard', icon: HomeIcon, proOnly: true },
    { name: 'My Products', href: 'products', icon: CubeIcon, proOnly: false },
    { name: 'Categories', href: 'categories', icon: TagIcon, proOnly: false },
    { name: 'Orders', href: 'orders', icon: ClipboardDocumentListIcon, proOnly: false },
    { name: 'Business Info', href: 'profile', icon: BuildingStorefrontIcon, proOnly: false },
    { name: 'Staffs', href: 'staffs', icon: UsersIcon, proOnly: false },
    { name: 'Tables', href: 'tables', icon: TableCellsIcon, proOnly: false },
    { name: 'Search Optimization', href: 'seo', icon: MagnifyingGlassIcon, proOnly: false },
    { name: 'Store Settings', href: 'settings', icon: Cog6ToothIcon, proOnly: false },
    { name: 'Live Store', href: 'store', icon: GlobeAltIcon, proOnly: false },
  ];

  // Primary navigation items for bottom bar (most important ones)
  const primaryNavigation = [
    // { name: 'Dashboard', href: 'dashboard', icon: HomeIcon, proOnly: true },
    { name: 'My Products', href: 'products', icon: CubeIcon, proOnly: false },
    { name: 'Orders', href: 'orders', icon: ClipboardDocumentListIcon, proOnly: false },
    { name: 'Categories', href: 'categories', icon: TagIcon, proOnly: false },
    { name: 'Business Info', href: 'profile', icon: BuildingStorefrontIcon, proOnly: false },
    { name: 'Tables', href: 'tables', icon: TableCellsIcon, proOnly: false },
    { name: 'More', href: 'more', icon: EllipsisHorizontalIcon, proOnly: false },
  ];

  // Filter navigation based on plan and type
  const getFilteredNavigation = () => {
    try {
      const restaurantDetails = localStorage.getItem('restaurantDetails');
      if (restaurantDetails) {
        const details = JSON.parse(restaurantDetails);
        // If plan is basic and type is not food, remove all pro menu items
        if (details.plan === 'basic' && details.type !== 'food') {
          return allNavigation.filter(item => !item.proOnly);
        }
      }
    } catch (error) {
      console.error('Error parsing restaurant details from localStorage:', error);
    }
    // Default: return all navigation items
    return allNavigation;
  };

  const staffRole = React.useMemo(() => getStaffRoleFromToken(), []);
  const canSeeAllPages = staffRole === 'shopOwner' || staffRole === 'manager';

  let navigation = getFilteredNavigation();
  // Frontend-only page visibility:
  // - shopOwner/manager can see everything
  // - everyone else can only access Orders page
  if (!canSeeAllPages) {
    navigation = navigation.filter((item) => item.href === 'orders');
  }
  
  // Filter primary navigation based on plan and availability
  const availablePrimaryItems = primaryNavigation.filter(item => {
    // Skip "More" button for now
    if (item.href === 'more') return false;
    
    // Check if user has access to this item
    const hasAccess = !item.proOnly || hasProPlan();
    const isAvailable = navigation.some(nav => nav.href === item.href);
    
    return hasAccess && isAvailable;
  });
  
  // For Pro users, prioritize Pro features first, then add other available features
  let prioritizedItems = availablePrimaryItems;
  if (hasProPlan()) {
    // Sort to show Pro items first, then others
    prioritizedItems = availablePrimaryItems.sort((a, b) => {
      if (a.proOnly && !b.proOnly) return -1;
      if (!a.proOnly && b.proOnly) return 1;
      return 0;
    });
  }
  
  // Take only the first 3 items for bottom navigation, then add "More"
  const filteredPrimaryNavigation = [
    ...prioritizedItems.slice(0, 3),
    { name: 'More', href: 'more', icon: EllipsisHorizontalIcon, proOnly: false }
  ];
  
  // Now create secondary navigation based on what's actually in the primary navigation
  const secondaryNavigation = allNavigation.filter(item => {
    // Don't include items that are actually in the filtered primary navigation
    const isInPrimary = filteredPrimaryNavigation.some(primary => primary.href === item.href);
    if (isInPrimary) return false;
    
    // Check if user has access to this item based on their plan
    const hasAccess = !item.proOnly || hasProPlan();
    
    return hasAccess;
  });
  
  // Filter secondary navigation based on availability
  const filteredSecondaryNavigation = secondaryNavigation.filter(item => {
    const isAvailable = navigation.some(nav => nav.href === item.href);
    return isAvailable;
  });

  const handleNavigationClick = (item: typeof allNavigation[0]) => {
    if (item.href === 'more') {
      if (!showMoreMenu) {
        window.history.pushState({ modalOpen: true }, '');
      }
      setShowMoreMenu(!showMoreMenu);
      return;
    }
    
    if (item.proOnly && !hasProPlan()) {
      // Show subscription modal for pro-only features
      if (onShowSubscriptionModal) {
        onShowSubscriptionModal();
      }
      return;
    }
    onPageChange(item.href);
    setShowMoreMenu(false);
  };

  const handleLogout = () => {
    logout();
  };

  const handleHomeClick = () => {
    onPageChange('products');
    setShowMoreMenu(false);
  };

  // Handle notification permission request
  const handleRequestPermission = async () => {
    const success = await initialize();
    if (success) {
      console.log('Notifications initialized successfully');
      setIsNotificationModalOpen(false);
    } else {
      console.log('Failed to initialize notifications');
    }
  };

  // Handle notification icon click on mobile - show modal instead of direct request
  const handleNotificationIconClick = () => {
    setIsNotificationModalOpen(true);
  };


  return (
    <div className="flex h-screen mobile-app-container">
      {/* Desktop sidebar — collapsible on large screens */}
      <div
        className={`hidden lg:flex lg:flex-shrink-0 overflow-hidden bg-white shadow-sm transition-[width] duration-200 ease-in-out ${
          desktopSidebarCollapsed ? 'w-0 border-r-0' : 'w-64 border-r border-gray-200'
        }`}
      >
        <div className="flex h-full w-64 flex-shrink-0 flex-col">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex h-16 items-center justify-between gap-2 px-3 bg-gradient-to-r from-orange-500 to-pink-600">
              <Link to="/" className="flex min-w-0 items-center space-x-2" onClick={handleHomeClick}>
                <div className="w-8 h-8 flex-shrink-0 bg-gradient-to-br from-orange-500 to-pink-600 rounded-lg flex items-center justify-center shadow-sm ring-2 ring-white/30">
                  <span className="text-white font-bold text-xl">M</span>
                </div>
                <span className="truncate text-xl font-bold text-white">Menunix</span>
              </Link>
              <button
                type="button"
                onClick={() => setDesktopSidebarCollapsed(true)}
                className="flex-shrink-0 rounded-lg p-2 text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/60"
                title="Close sidebar"
                aria-label="Close sidebar"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 px-4 py-4">
              {navigation.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigationClick(item)}
                  className={`w-full flex items-center px-4 py-3 text-left rounded-lg mb-2 transition-colors ${
                    currentPage === item.href
                      ? 'bg-orange-100 text-orange-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  } ${!hasProPlan() && item.proOnly ? 'opacity-60' : ''}`}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  <span className="flex-1">{item.name}</span>
                </button>
              ))}
            </nav>
            <div className="border-t border-gray-200 p-4">
              {/* Notification Settings - Desktop Sidebar Only */}
              {hasProPlan() && isSupported && !isPermissionGranted && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center">
                    <BellIcon className="h-5 w-5 text-orange-400 mr-2" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-orange-800">Enable Notifications</p>
                      <p className="text-xs text-orange-600">Get notified about new orders</p>
                    </div>
                    <button
                      onClick={handleRequestPermission}
                      className="bg-orange-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-pink-600"
                    >
                      Enable
                    </button>
                  </div>
                </div>
              )}
              
              <div className="flex items-center mb-4">
                <UserIcon className="h-8 w-8 text-gray-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500">{user?.mobile}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 text-left text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile App Layout */}
      <div className="flex flex-1 flex-col lg:ml-0">
        {/* Mobile Header */}
        <div className="lg:hidden mobile-header">
          <div className="flex h-16 items-center justify-between px-4">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2" onClick={handleHomeClick}>
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">M</span>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">
                  Menunix
                </span>
              </Link>
              {subscription?.status === 'active' && (
                <span className="ml-2 inline-flex px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                  Pro
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {/* Notification Settings - Mobile: Show modal, Desktop: Direct enable */}
              {hasProPlan() && isSupported && !isPermissionGranted && (
                <button
                  onClick={handleNotificationIconClick}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-orange-50 rounded-full transition-colors"
                  title="Enable Notifications"
                >
                  <BellIcon className="h-5 w-5" />
                </button>
              )}
              {canViewStoreLiveNav && (
                <button
                  onClick={() => {
                    window.history.pushState({ modalOpen: true }, '');
                    setIsVisitModalOpen(true);
                  }}
                  className="p-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-full transition-colors"
                  title="Your store is live"
                >
                  <GlobeAltIcon className="h-5 w-5" />
                </button>
              )}
              <div className="flex items-center space-x-2">
                <UserIcon className="h-6 w-6 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">{user?.name || 'Owner'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:block bg-white shadow-sm border-b border-gray-200">
          <div className="flex h-16 items-center justify-between px-6">
            <div className="flex items-center gap-2">
              {desktopSidebarCollapsed && (
                <button
                  type="button"
                  onClick={() => setDesktopSidebarCollapsed(false)}
                  className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1"
                  title="Open sidebar"
                  aria-label="Open sidebar"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              )}
              <Link to="/" className="flex items-center space-x-2" onClick={handleHomeClick}>
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">M</span>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">
                  Menunix
                </span>
              </Link>
              {subscription?.status === 'active' && (
                <span className="ml-2 inline-flex px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                  Pro
                </span>
              )}
            </div>
            <div className="flex items-center space-x-4">
              {/* Notification Settings */}
              {hasProPlan() && isSupported && !isPermissionGranted && (
                <button
                  onClick={handleRequestPermission}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-orange-50 rounded-full transition-colors"
                >
                  <BellIcon className="h-5 w-5" />
                </button>
              )}
              {canViewStoreLiveNav && (
                <button
                  onClick={() => {
                    window.history.pushState({ modalOpen: true }, '');
                    setIsVisitModalOpen(true);
                  }}
                  className="inline-flex items-center px-3 py-2 border border-orange-200 rounded-md text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 hover:border-orange-300 transition-colors"
                >
                  <GlobeAltIcon className="h-4 w-4 mr-2" />
                  Your store is live
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />
                </button>
              )}
              <div className="flex items-center space-x-2">
                <UserIcon className="h-6 w-6 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">{user?.name || 'Owner'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Main Content */}
        <main className="mobile-content lg:pb-6">
          <div className="p-4 lg:p-6">
            {children}
          </div>
        </main>

        {/* Mobile Bottom Navigation */}
        <div className="lg:hidden mobile-bottom-nav">
          <div className="flex">
            {filteredPrimaryNavigation.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigationClick(item)}
                className={`mobile-nav-item ${
                  currentPage === item.href || (item.href === 'more' && showMoreMenu)
                    ? 'active'
                    : ''
                } ${!hasProPlan() && item.proOnly ? 'opacity-60' : ''}`}
                style={{ position: 'relative' }}
              >
                <item.icon className="mobile-nav-icon" />
                <span className="mobile-nav-label">{item.name}</span>
                {/* Visual indicator for More button */}
                {item.href === 'more' && (
                  <div className="absolute -top-1 -right-1 h-2 w-2 bg-orange-500 rounded-full animate-pulse"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile More Menu */}
        {showMoreMenu && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div 
              className="mobile-more-overlay" 
              onClick={() => {
                if (window.history.state?.modalOpen) {
                  window.history.back();
                }
                setShowMoreMenu(false);
              }} 
            />
            <div className="mobile-more-menu">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">More Options</h3>
                  <button
                    onClick={() => {
                      if (window.history.state?.modalOpen) {
                        window.history.back();
                      }
                      setShowMoreMenu(false);
                    }}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="space-y-2">
                  {filteredSecondaryNavigation.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => handleNavigationClick(item)}
                      className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                        currentPage === item.href
                          ? 'bg-orange-100 text-orange-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      } ${!hasProPlan() && item.proOnly ? 'opacity-60' : ''}`}
                    >
                      <item.icon className="h-5 w-5 mr-3" />
                      <span className="flex-1">{item.name}</span>
                    </button>
                  ))}
                  <div className="border-t border-gray-200 pt-2 mt-4">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-3 text-left text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Visit Site Modal — owner & manager only */}
      {isVisitModalOpen && canViewStoreLiveNav && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div 
            onClick={() => {
              if (window.history.state?.modalOpen) {
                window.history.back();
              }
              setIsVisitModalOpen(false);
            }} 
            className="absolute inset-0" 
          />
          <div className="bg-white rounded-lg max-w-lg w-full overflow-hidden relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Your store is live</h3>
                <button 
                  onClick={() => {
                    if (window.history.state?.modalOpen) {
                      window.history.back();
                    }
                    setIsVisitModalOpen(false);
                  }} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4">
                <StoreUrlBlock />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Enable Modal - Mobile Only */}
      {isNotificationModalOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div onClick={() => setIsNotificationModalOpen(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-sm w-full overflow-hidden relative">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <BellIcon className="h-6 w-6 text-orange-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Enable Notifications</h3>
                </div>
                <button 
                  onClick={() => setIsNotificationModalOpen(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Get notified about new orders
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsNotificationModalOpen(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestPermission}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-600"
                >
                  Enable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Layout: React.FC<LayoutProps> = (props) => (
  <LayoutProvider>
    <LayoutShell {...props} />
  </LayoutProvider>
);

export default Layout;